import React, {
  HTMLAttributes,
  createRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import useOutsideClick from '../../hooks/useOutsideClick/useOutsideClick';
import { getUniqueKey } from '../../utils/HTMLutils';
import './DropdownMenu.scss';
const rootClassName = 'component-dropdown-menu';

export interface DropdownMenuProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'content'> {
  opened?: boolean;
  setOpened: (evt: boolean) => void;
  content?: React.ReactNode;
  closeOnOutClick?: boolean;
  closeOnEsc?: boolean;
  panelProps?: HTMLAttributes<HTMLDivElement>;
}

export default function DropdownMenu({
  opened,
  setOpened,
  content,
  closeOnEsc,
  closeOnOutClick,
  children,
  panelProps = {},
  ...props
}: DropdownMenuProps) {
  const id = useMemo(() => {
    return props.id ? props.id : getUniqueKey();
  }, [props.id]);
  const [inter, setInter] = useState<any>(null);

  const wrapperRef = createRef<HTMLDivElement>();
  const panelRef = createRef<HTMLDivElement>();

  const interRef = useRef(inter);
  interRef.current = inter;

  // Position
  useEffect(() => {
    const current = interRef.current;
    if (current) {
      clearInterval(current);
      setInter(null);
    }
    const position = () => {
      const painel = document.querySelector(
        `#${id} .${rootClassName}-panel`
      ) as HTMLElement;
      if (!painel) return;

      const trigger = document.querySelector(
        `#${id} .${rootClassName}-children > *`
      ) as HTMLElement;
      const bound = trigger.getBoundingClientRect();
      const pontoCentralX = bound.x + bound.width / 2;
      const pontoCentralY = bound.y + bound.height / 2;
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      if (pontoCentralX < windowWidth / 2) {
        painel.style.left = `${bound.x}px`;
        if (pontoCentralY < windowHeight / 2) {
          painel.style.top = `${bound.y + bound.height}px`;
        } else {
          painel.style.top = `${
            bound.y - painel.getBoundingClientRect().height
          }px`;
        }
      } else {
        painel.style.right = `${windowWidth - bound.x - bound.width}px`;
        if (pontoCentralY < windowHeight / 2) {
          painel.style.top = `${bound.y + bound.height}px`;
        } else {
          painel.style.top = `${
            bound.y - painel.getBoundingClientRect().height
          }px`;
        }
      }
    };
    if (opened) {
      position();
      const inter = setInterval(position, 100);
      setInter(inter);
    }
    return () => {
      if (interRef.current) {
        clearInterval(interRef.current);
        setInter(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  useOutsideClick([wrapperRef], (isInside) => {
    if (closeOnOutClick && !isInside) {
      setOpened(false);
    }
  });

  useEffect(() => {
    const onEscPress = (evt: any) => {
      if (evt.key === 'Escape') {
        setOpened(false);
      }
    };

    if (closeOnEsc) {
      if (opened) {
        document.addEventListener('keyup', onEscPress);
      } else {
        document.removeEventListener('keyup', onEscPress);
      }
    }
    return () => {
      document.removeEventListener('keyup', onEscPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  return (
    <div
      {...props}
      ref={wrapperRef}
      id={id}
      className={[rootClassName, props.className || ''].join(' ')}
    >
      <div className={`${rootClassName}-children`}>{children}</div>
      <CSSTransition
        in={opened}
        timeout={300}
        classNames={`${rootClassName}-panel-fade`}
        unmountOnExit
      >
        <div
          ref={panelRef}
          {...panelProps}
          className={[
            `${rootClassName}-panel scroll-white`,
            panelProps?.className ?? '',
          ].join(' ')}
        >
          {content}
        </div>
      </CSSTransition>
    </div>
  );
}

DropdownMenu.defaultProps = {
  closeOnOutClick: true,
  closeOnEsc: true,
};
